import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { User } from '../../types'
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  ClipboardList,
  UserCog,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Plane
} from 'lucide-react'

interface LayoutProps {
  user: User
  onLogout: () => void
  children: React.ReactNode
}

export default function Layout({ user, onLogout, children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()

  const isAdmin = user.role === 'superadmin' || user.role === 'admin'
  const isStaff = user.role === 'staff'

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/projects', label: 'Projects', icon: FolderOpen },
    ...(isAdmin || isStaff ? [
      { path: '/admin/clients', label: 'Clients', icon: Users },
      { path: '/admin/users', label: 'Users', icon: UserCog },
      { path: '/admin/audit', label: 'Audit Log', icon: ClipboardList },
    ] : []),
  ]

  return (
    <div className="min-h-screen bg-surface flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-navy-900 text-white transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'
        } lg:static lg:w-64 flex flex-col`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-navy-700">
            <Plane className="w-7 h-7 text-orange-500 mr-3" />
            <span className="text-xl font-bold tracking-tight">DroneScape</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-orange-500/10 text-orange-500'
                      : 'text-navy-400 hover:text-white hover:bg-navy-800'
                  }`}
                >
                  <Icon size={18} className="mr-3" />
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-navy-700">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center w-full text-left hover:bg-navy-800 rounded-lg p-2 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold mr-3">
                  {user.first_name?.[0] || user.email[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-navy-400 truncate">{user.role}</p>
                </div>
                <ChevronDown size={14} className="text-navy-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-navy-800 rounded-lg shadow-lg border border-navy-700 py-1">
                  <button
                    onClick={onLogout}
                    className="flex items-center w-full px-3 py-2 text-sm text-red-400 hover:bg-navy-700 rounded-lg"
                  >
                    <LogOut size={14} className="mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-100 flex items-center px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-navy-600 hover:bg-gray-50 rounded-lg mr-4"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h2 className="text-lg font-semibold text-navy-800">
            {navItems.find(n => n.path === location.pathname)?.label || 'DroneScape'}
          </h2>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-100 px-4 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Plane size={14} className="text-orange-500" />
              <span className="font-medium text-navy-600">DroneScape</span>
              <span>– Dronemaps</span>
            </div>
            <p>© {new Date().getFullYear()} DRONESCAPE. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
